import { z } from "zod";
import { DOCUMENT_FAILURE_REASONS } from "./documents.ts";
import type { CitationStatus } from "./knowledge.ts";
import { MODEL_ROLES } from "./models.ts";
import { toolIdSchema } from "./tools.ts";

/**
 * Usage reporting (spec `usage-reporting`): counts, sums and shares only. No schema here has a
 * free-text field other than the names of entities, projects and tenants the viewer may already
 * list; every other string is an ID, a date or a closed enum (asserted by `reporting.test.ts`).
 */

/**
 * What a metered model call is billed under: a model role, or `embedding` for the pinned
 * embedding model (not a role: it never falls back). Never a provider model ID.
 */
export const USAGE_ROLES = [...MODEL_ROLES, "embedding"] as const;
export const usageRoleSchema = z.enum(USAGE_ROLES);
export type UsageRole = z.infer<typeof usageRoleSchema>;

export const MODEL_CALL_OUTCOMES = ["ok", "error", "unavailable"] as const;
export type ModelCallOutcome = (typeof MODEL_CALL_OUTCOMES)[number];

/** Where a metered model call came from: the API, the worker, or the MCP server (mcp-server-core). */
export const USAGE_ORIGINS = ["api", "worker", "mcp"] as const;
export type UsageOrigin = (typeof USAGE_ORIGINS)[number];

/**
 * A health probe's usage (openspec change `model-picking`, D6, D14): tracked, never counted
 * against a plan. Not a reported origin yet: reports count it as `other` (the report schema of
 * the live release does not know it).
 */
export const HEALTH_PROBE_ORIGIN = "health_probe";
/** The origin a usage event is written with. */
export type UsageEventOrigin = UsageOrigin | typeof HEALTH_PROBE_ORIGIN;

/** A report's origin line: an origin this release knows, or `other` for one it does not. */
export const REPORTED_ORIGINS = [...USAGE_ORIGINS, "other"] as const;
export type ReportedOrigin = (typeof REPORTED_ORIGINS)[number];

/** How a citation counts in the quality report. */
export const CITATION_QUALITIES = ["supported", "unverified", "unsupported"] as const;
export type CitationQuality = (typeof CITATION_QUALITIES)[number];

/**
 * A citation as the quality report counts it: `unchecked` is unverified, and so is a
 * `supported` citation of a passage the transcription guard flagged (OCR). `unsupported` stays.
 */
export function effectiveCitationStatus(
  status: CitationStatus,
  provenance: { flagged?: boolean } | null | undefined,
): CitationQuality {
  if (status === "unsupported") {
    return "unsupported";
  }
  return status === "supported" && provenance?.flagged !== true ? "supported" : "unverified";
}

/** Raw usage events are kept 90 days; daily figures 13 months. */
export const USAGE_EVENT_RETENTION_DAYS = 90;
export const ROLLUP_RETENTION_MONTHS = 13;
/** Figures older than this are flagged as stale (they are recomputed hourly). */
export const REPORT_STALE_AFTER_MS = 2 * 60 * 60 * 1000;
/** The longest period a report covers. */
export const REPORT_MAX_DAYS = 397;

const daySchema = z.iso.date();
const uuid = z.uuid();
const count = z.int().min(0);
/** USD, estimated: never negative, not necessarily whole. */
const usd = z.number().min(0);
const share = z.number().min(0).max(1);

export const reportQuerySchema = z
  .object({
    /** First day (UTC), inclusive. Default: 29 days before `to`. */
    from: daySchema.optional(),
    /** Last day (UTC), inclusive. Default: today. */
    to: daySchema.optional(),
    /** Only this entity and its descendants (it must be in the caller's scope). */
    entity: uuid.optional(),
  })
  .strict();
export type ReportQuery = z.infer<typeof reportQuerySchema>;

export const operatorReportQuerySchema = reportQuerySchema.omit({ entity: true }).extend({
  /** One tenant only. */
  tenant: uuid.optional(),
});
export type OperatorReportQuery = z.infer<typeof operatorReportQuerySchema>;

/** When the figures were last computed; `stale` once older than two hours (or never). */
export const reportFreshnessSchema = z
  .object({ computedAt: z.iso.datetime().nullable(), stale: z.boolean() })
  .strict();
export type ReportFreshness = z.infer<typeof reportFreshnessSchema>;

const reportPeriodSchema = z.object({ from: daySchema, to: daySchema }).strict();

/** An entity in the caller's scope; `name` is the one free-text field allowed with projects'. */
const entityRefSchema = z
  .object({ entityId: uuid, name: z.string(), parentId: uuid.nullable() })
  .strict();

/**
 * A project in scope, or, with every field null, all personal projects together (tenant admins
 * only): a personal project is never listed alone, its figures would be one person's. With
 * `standalone`, an entity's "Standalone conversations" line: the figures of conversations
 * outside any project anchored to `entityId`, never naming a conversation or a user.
 */
const projectRefSchema = z
  .object({
    projectId: uuid.nullable(),
    name: z.string().nullable(),
    entityId: uuid.nullable(),
    standalone: z.boolean().optional(),
  })
  .strict();

/**
 * True when any figure of the report's period and scope is synthetic demo data (demo-world-v2):
 * the report then says so. Optional for one release (an older API revision does not send it).
 */
const syntheticFlag = z.boolean().optional();

const reportBase = {
  period: reportPeriodSchema,
  freshness: reportFreshnessSchema,
  /** The entity the report is narrowed to, or null for the caller's whole scope. */
  entityId: uuid.nullable(),
  synthetic: syntheticFlag,
};

// ── Usage ─────────────────────────────────────────────────────────────────────────────────

const usageDaySchema = z
  .object({ day: daySchema, activeUsers: count, conversations: count, questions: count })
  .strict();
/** Distinct active users per ISO week (its Monday) or calendar month (its first day). */
const activeUsersPeriodSchema = z.object({ start: daySchema, activeUsers: count }).strict();
const usageFiguresSchema = z.object({ conversations: count, questions: count }).strict();

export const usageReportSchema = z
  .object({
    ...reportBase,
    daily: z.array(usageDaySchema),
    weekly: z.array(activeUsersPeriodSchema),
    monthly: z.array(activeUsersPeriodSchema),
    totals: usageFiguresSchema,
    entities: z.array(entityRefSchema.extend(usageFiguresSchema.shape).strict()),
    /** The ten projects with the most questions in the period. */
    topProjects: z.array(projectRefSchema.extend(usageFiguresSchema.shape).strict()),
  })
  .strict();
export type UsageReport = z.infer<typeof usageReportSchema>;

// ── Answer quality ────────────────────────────────────────────────────────────────────────

const qualityFiguresSchema = z
  .object({
    supported: count,
    unverified: count,
    unsupported: count,
    answers: count,
    answersWithoutSource: count,
    /** Citations of passages flagged by the transcription guard. */
    ocrFlagged: count,
    /** Web citations (`W#`), counted apart: they never change the knowledge figures above. */
    webSupported: count,
    webUnverified: count,
    webUnsupported: count,
    /** Answers that cite web results. */
    answersWithWeb: count,
  })
  .strict();
export type QualityFigures = z.infer<typeof qualityFiguresSchema>;

export const qualityReportSchema = z
  .object({
    ...reportBase,
    daily: z.array(qualityFiguresSchema.extend({ day: daySchema }).strict()),
    totals: qualityFiguresSchema,
    /** Shares of the period's citations (0 when there is none), and of its answers. */
    shares: z
      .object({
        supported: share,
        unverified: share,
        unsupported: share,
        answersWithoutSource: share,
        /** Shares of the period's web citations (0 when there is none). */
        webSupported: share,
        webUnverified: share,
        webUnsupported: share,
        answersWithWeb: share,
      })
      .strict(),
    entities: z.array(entityRefSchema.extend(qualityFiguresSchema.shape).strict()),
    projects: z.array(projectRefSchema.extend(qualityFiguresSchema.shape).strict()),
  })
  .strict();
export type QualityReport = z.infer<typeof qualityReportSchema>;

// ── Cost and capacity ─────────────────────────────────────────────────────────────────────

const roleCostSchema = z
  .object({
    role: usageRoleSchema,
    inputTokens: count,
    outputTokens: count,
    cacheReadTokens: count,
    cacheWriteTokens: count,
    /** Calls of known cost only. */
    costUsd: usd,
    calls: count,
    /** Calls whose cost could not be estimated: shown apart, never added as zero. */
    unknownCostCalls: count,
  })
  .strict();
export type RoleCost = z.infer<typeof roleCostSchema>;

/** A use against its limit; `limit` is null when none is set. */
const capacitySchema = z.object({ used: count, limit: count.nullable() }).strict();

/** Outbound tool calls and their estimated cost, per tool (agent-tools-platform). */
const toolCostSchema = z
  .object({ tool: toolIdSchema, calls: count, costUsd: usd, unknownCostCalls: count })
  .strict();
export type ToolCost = z.infer<typeof toolCostSchema>;

/** Model cost per origin (`mcp`: calls made through the MCP server). */
const originCostSchema = z
  .object({ origin: z.enum(REPORTED_ORIGINS), calls: count, costUsd: usd })
  .strict();
export type OriginCost = z.infer<typeof originCostSchema>;

export const costReportSchema = z
  .object({
    ...reportBase,
    daily: z.array(
      z
        .object({
          day: daySchema,
          roles: z.array(roleCostSchema.pick({ role: true, costUsd: true }).strict()),
          inputTokens: count,
          outputTokens: count,
          costUsd: usd,
        })
        .strict(),
    ),
    roles: z.array(roleCostSchema),
    totals: z.object({ costUsd: usd, unknownCostCalls: count }).strict(),
    /** Tool calls, apart from model cost (`costUsd` above is models only). */
    tools: z.array(toolCostSchema),
    /** Model cost per origin, e.g. an "MCP" line (optional for one release). */
    origins: z.array(originCostSchema).optional(),
    entities: z.array(
      entityRefSchema
        .extend({ costUsd: usd, tools: z.array(toolCostSchema.omit({ unknownCostCalls: true })) })
        .strict(),
    ),
    projects: z.array(projectRefSchema.extend({ costUsd: usd }).strict()),
    /** Tenant-wide limits: present for tenant admins only. */
    capacity: z
      .object({
        /** `YYYY-MM` of the OCR month. */
        ocrMonth: z.string().regex(/^\d{4}-\d{2}$/),
        ocrPages: capacitySchema,
        storageBytes: capacitySchema,
        /** Active accounts (support accounts excluded) against seats. */
        activeUsers: capacitySchema,
        /**
         * This month's estimated model spend against the plan's limit (spec `tenant-plans`):
         * `exceeded` once live spend alone reaches it, `hard` when the plan then refuses new
         * turns; `warning` from 80 % of the displayed total.
         */
        modelSpend: z
          .object({
            /** Live plus synthetic demo spend (demo-world-v2): the displayed total. */
            usd,
            limit: usd.nullable(),
            exceeded: z.boolean(),
            warning: z.boolean(),
            hard: z.boolean(),
            unknownCostCalls: count,
            /** The synthetic demo spend in `usd` (0 without). Optional for one release. */
            syntheticUsd: usd.optional(),
          })
          .strict(),
      })
      .strict()
      .nullable(),
    /** Stored bytes of live documents in scope (latest snapshot). */
    storageBytes: count,
  })
  .strict();
export type CostReport = z.infer<typeof costReportSchema>;

// ── Health and security ───────────────────────────────────────────────────────────────────

const healthFiguresSchema = z
  .object({
    ingestionFailures: count,
    modelFailures: count,
    modelUnavailable: count,
    modelFallbacks: count,
    notFound: count,
    downloadDenied: count,
    flowDenied: count,
    breakGlass: count,
    declassificationsCreated: count,
    declassificationsWithdrawn: count,
    /** Outbound tool calls blocked by the egress gate, and declined by their user. */
    toolCallsBlocked: count,
    toolCallsDeclined: count,
  })
  .strict();
export type HealthFigures = z.infer<typeof healthFiguresSchema>;

/** Ingestion failure reasons as reported: the documents' closed set, anything else `other`. */
export const INGESTION_FAILURE_REASONS = [...DOCUMENT_FAILURE_REASONS, "other"] as const;
export type IngestionFailureReason = (typeof INGESTION_FAILURE_REASONS)[number];

export const healthReportSchema = z
  .object({
    ...reportBase,
    daily: z.array(healthFiguresSchema.extend({ day: daySchema }).strict()),
    totals: healthFiguresSchema,
    ingestionFailures: z.array(
      z.object({ reason: z.enum(INGESTION_FAILURE_REASONS), count }).strict(),
    ),
    /** Documents waiting to be processed (latest snapshot). */
    pending: z.object({ uploading: count, processing: count }).strict(),
    entities: z.array(entityRefSchema.extend(healthFiguresSchema.shape).strict()),
    projects: z.array(projectRefSchema.extend(healthFiguresSchema.shape).strict()),
  })
  .strict();
export type HealthReport = z.infer<typeof healthReportSchema>;

// ── Operators: per tenant, totals only ────────────────────────────────────────────────────

const tenantRefSchema = z.object({ tenantId: uuid, name: z.string() }).strict();
const operatorBase = {
  period: reportPeriodSchema,
  /** The oldest freshness among the tenants listed. */
  freshness: reportFreshnessSchema,
};

/** A tenant's figures: `synthetic` when any of them is synthetic demo data (demo-world-v2). */
const operatorTenantBase = tenantRefSchema
  .extend({ freshness: reportFreshnessSchema, synthetic: syntheticFlag })
  .strict();

export const operatorUsageReportSchema = z
  .object({
    ...operatorBase,
    tenants: z.array(
      operatorTenantBase
        .extend({
          daily: z.array(usageDaySchema.extend({ costUsd: usd }).strict()),
          monthly: z.array(activeUsersPeriodSchema),
          totals: usageFiguresSchema.extend({ costUsd: usd }).strict(),
        })
        .strict(),
    ),
  })
  .strict();
export type OperatorUsageReport = z.infer<typeof operatorUsageReportSchema>;

export const operatorQualityReportSchema = z
  .object({
    ...operatorBase,
    tenants: z.array(operatorTenantBase.extend({ totals: qualityFiguresSchema }).strict()),
  })
  .strict();
export type OperatorQualityReport = z.infer<typeof operatorQualityReportSchema>;

/** An instance spend line: its estimated USD, calls and calls of unknown cost. */
const spendFigures = z.object({ usd, calls: count, unknownCalls: count }).strict();

export const operatorCostReportSchema = z
  .object({
    ...operatorBase,
    tenants: z.array(
      operatorTenantBase
        .extend({
          roles: z.array(roleCostSchema),
          totals: z.object({ costUsd: usd, unknownCostCalls: count }).strict(),
          tools: z.array(toolCostSchema),
          /** Model cost per origin (optional for one release). */
          origins: z.array(originCostSchema).optional(),
          /**
           * This calendar month's spend counter (not a rollup): models and tools, live plus
           * synthetic demo spend (`syntheticUsd`, demo-world-v2; optional for one release).
           */
          monthSpend: z
            .object({
              month: z.string().regex(/^\d{4}-\d{2}$/),
              usd,
              unknownCalls: count,
              syntheticUsd: usd.optional(),
            })
            .strict(),
          storageBytes: count,
        })
        .strict(),
    ),
    /**
     * The instance's own model spend over the period, attributed to no tenant
     * (feedback-triage-assist D5: the triage assistant); absent from an older API.
     */
    instance: z
      .object({
        feedbackTriage: spendFigures
          .extend({
            /** Today's spend (UTC), whatever the period (#410; optional for one release). */
            today: spendFigures.extend({ day: daySchema }).strict().optional(),
            /** This calendar month's spend, whatever the period (#410; optional for one release). */
            month: spendFigures
              .extend({ month: z.string().regex(/^\d{4}-\d{2}$/) })
              .strict()
              .optional(),
          })
          .strict(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type OperatorCostReport = z.infer<typeof operatorCostReportSchema>;

export const operatorHealthReportSchema = z
  .object({
    ...operatorBase,
    tenants: z.array(
      operatorTenantBase
        .extend({
          totals: healthFiguresSchema,
          pending: z.object({ uploading: count, processing: count }).strict(),
        })
        .strict(),
    ),
  })
  .strict();
export type OperatorHealthReport = z.infer<typeof operatorHealthReportSchema>;

/** Report names, as in `/api/admin/reports/:report` and `/api/operator/reports/:report`. */
export const REPORT_KINDS = ["usage", "quality", "cost", "health"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];
